// src/sim/ai/blackboard.ts — per-team blackboard pure reducers (D-13, D-14).
//
// All functions are PURE: they return new TeamBlackboard objects (or the SAME
// reference when nothing changed — Pitfall 11 identity pass-through).
//
// Source: .planning/phases/04-ai/04-CONTEXT.md D-13..D-14;
//         04-RESEARCH.md Pattern 7 (blackboard design L745-807).

import type { TeamBlackboard, BlackboardEntry } from '../../shared/types/world';
import type { Vec2 } from '../../shared/types/intent';
import {
  P4_BLACKBOARD_LOW_INITIAL_SIGMA_PX,
  P4_BLACKBOARD_MEDIUM_INITIAL_SIGMA_PX,
  P4_BLACKBOARD_HIGH_INITIAL_SIGMA_PX,
} from './coordination/constants';
import { gaussianPair } from './coordination/gaussian';
// P7 POL-06 / D-12: blackboard max-age + sigma growth read through the tuning
// cache (./coordination/constants are the fallback defaults). Initial-sigma-px
// values stay code constants (not admin-editable).
import { getTuning } from '../content/cache';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Returns the initial sigma (px) for the given confidence level.
 * high = 16 px, medium = 32 px, low = 64 px (D-14).
 */
function sigmaForConfidence(conf: 'low' | 'medium' | 'high'): number {
  if (conf === 'high') return P4_BLACKBOARD_HIGH_INITIAL_SIGMA_PX;
  if (conf === 'medium') return P4_BLACKBOARD_MEDIUM_INITIAL_SIGMA_PX;
  return P4_BLACKBOARD_LOW_INITIAL_SIGMA_PX;
}

/**
 * Returns the pixel distance between two Vec2 positions.
 */
function dist(a: Vec2, b: Vec2): number {
  return Math.hypot(b.x - a.x, b.y - a.y);
}

// Threshold for "same position" upgrade check (roughly 2 tiles at 32 px/tile = 64 px)
const UPGRADE_DISTANCE_THRESHOLD_PX = 64;

// ---------------------------------------------------------------------------
// Exported reducers
// ---------------------------------------------------------------------------

/**
 * Append a new entry to the blackboard. Returns a new TeamBlackboard object.
 * The entry's id is derived from bb.nextId formatted as 'bb-{n}'.
 *
 * @param bb - Current blackboard state.
 * @param partial - Entry fields (id will be assigned from nextId).
 */
export function writeBlackboardEntry(
  bb: TeamBlackboard,
  partial: Omit<BlackboardEntry, 'id'>,
): TeamBlackboard {
  const entry: BlackboardEntry = {
    ...partial,
    id: `bb-${bb.nextId}`,
  };
  return {
    entries: [...bb.entries, entry],
    nextId: bb.nextId + 1,
  };
}

/**
 * Upgrade an existing entry if the same observer has a nearby entry, otherwise write new.
 * "Nearby" = within UPGRADE_DISTANCE_THRESHOLD_PX pixels AND same observedByEntityId.
 * Upgrade means: update position, initialSigmaPx, confidence, createdAtTick.
 *
 * Returns a new TeamBlackboard (even on upgrade — shallow copy of entries array).
 */
export function upgradeOrWriteBlackboardEntry(
  bb: TeamBlackboard,
  observerId: string,
  position: Vec2,
  newConfidence: 'low' | 'medium' | 'high',
  tick: number,
): TeamBlackboard {
  const idx = bb.entries.findIndex(
    e => e.observedByEntityId === observerId && dist(e.position, position) <= UPGRADE_DISTANCE_THRESHOLD_PX,
  );

  if (idx === -1) {
    // No matching entry — write new
    return writeBlackboardEntry(bb, {
      position,
      initialSigmaPx: sigmaForConfidence(newConfidence),
      createdAtTick: tick,
      confidence: newConfidence,
      observedByEntityId: observerId,
    });
  }

  // Upgrade in-place (shallow copy of entries array)
  const existing = bb.entries[idx]!;
  const upgraded: BlackboardEntry = {
    ...existing,
    position,
    initialSigmaPx: sigmaForConfidence(newConfidence),
    createdAtTick: tick,
    confidence: newConfidence,
  };
  const newEntries = bb.entries.slice();
  newEntries[idx] = upgraded;
  return { entries: newEntries, nextId: bb.nextId };
}

/**
 * Drop entries older than P4_BLACKBOARD_MAX_AGE_TICKS.
 * Returns the SAME reference (identity pass-through) if nothing was pruned — Pitfall 11.
 */
export function pruneBlackboard(bb: TeamBlackboard, currentTick: number): TeamBlackboard {
  const maxAgeTicks = getTuning().blackboardMaxAgeTicks;
  const survivors = bb.entries.filter(e => currentTick - e.createdAtTick <= maxAgeTicks);
  if (survivors.length === bb.entries.length) return bb; // Pitfall 11: identity pass-through
  return { entries: survivors, nextId: bb.nextId };
}

/**
 * The effective Gaussian sigma at a given tick:
 *   effectiveSigma = initialSigmaPx + P4_BLACKBOARD_SIGMA_GROWTH_PER_TICK * (currentTick - createdAtTick)
 */
export function effectiveSigma(entry: BlackboardEntry, currentTick: number): number {
  const age = Math.max(0, currentTick - entry.createdAtTick);
  return entry.initialSigmaPx + getTuning().blackboardSigmaGrowthPerTick * age;
}

/**
 * Sample a jittered target position near entry.position by adding Gaussian noise
 * scaled by effectiveSigma. Uses the provided [0,1) RNG (must be seeded for determinism).
 *
 * Returns a Vec2 representing the jittered target in pixel space.
 */
export function sampleJitteredTarget(
  entry: BlackboardEntry,
  currentTick: number,
  rng: () => number,
): Vec2 {
  const sigma = effectiveSigma(entry, currentTick);
  const [gx, gy] = gaussianPair(rng);
  return {
    x: entry.position.x + gx * sigma,
    y: entry.position.y + gy * sigma,
  };
}
